import { useMemo } from 'react';
import { Box, Grid, Stack, Typography, useTheme } from '@mui/material';
import { useAppSelector } from 'hooks/redux.hooks';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { getWebhookMonitoringHealthState } from 'state/reducers/webhook-monitoring.reducer';
import { WebhookMonitoringReplayReason } from 'constants/webhook-monitoring.constants';
import { webhookMonitoringDashboardPanelSx } from './dashboardPanelSx';

const TABULAR_NUMS = { fontVariantNumeric: 'tabular-nums' as const };

const getMetricColor = (value: number, theme: any) =>
	value > 0 ? theme.palette.root.error : theme.palette.root.basicDeeoGreen;

export const QueueHealthSection = () => {
	const theme = useTheme();
	const t = useAppSelector(selectTranslations);
	const health = useAppSelector(getWebhookMonitoringHealthState);

	const metricItems = useMemo(() => {
		if (!health.data) {
			return [];
		}

		const counts = health.data.operations.replayEligibilityCounts;
		return [
			{
				key: 'replayableTotal',
				labelKey: 'page.webhookMonitoring.dashboard.metric.replayableTotal',
				value: counts.total,
			},
			{
				key: 'queuedReplayable',
				labelKey: 'page.webhookMonitoring.dashboard.metric.queuedReplayable',
				value:
					counts[WebhookMonitoringReplayReason.FailedPublish] +
					counts[WebhookMonitoringReplayReason.StalePending] +
					counts[WebhookMonitoringReplayReason.StalePublished],
			},
			{
				key: 'processingReplayable',
				labelKey: 'page.webhookMonitoring.dashboard.metric.processingReplayable',
				value:
					counts[WebhookMonitoringReplayReason.FailedProcessing] +
					counts[WebhookMonitoringReplayReason.StaleProcessing],
			},
			{
				key: WebhookMonitoringReplayReason.DeadLetter,
				labelKey: `page.webhookMonitoring.replayReason.${WebhookMonitoringReplayReason.DeadLetter}`,
				value: counts[WebhookMonitoringReplayReason.DeadLetter],
			},
		];
	}, [health.data]);

	if (!health.data) {
		return null;
	}

	return (
		<Box sx={webhookMonitoringDashboardPanelSx}>
			<Typography variant="h4" sx={{ mb: 2 }}>
				{t('page.webhookMonitoring.dashboard.metrics.title')}
			</Typography>
			<Grid container spacing={3}>
				{metricItems.map(({ key, labelKey, value }) => {
					const label = t(labelKey);
					return (
						<Grid item xs={12} sm={6} md={4} lg key={key}>
							<Stack spacing={0.5}>
								<Typography variant="boldBody4" sx={{ color: theme.palette.root.grey }}>
									{label}
								</Typography>
								<Typography
									variant="boldBody3"
									aria-label={`${label}: ${value}`}
									sx={{
										...TABULAR_NUMS,
										color: getMetricColor(value, theme),
									}}
								>
									{value}
								</Typography>
							</Stack>
						</Grid>
					);
				})}
			</Grid>
		</Box>
	);
};
